import type { ClinicalBridgeRules, ClinicalBridgeStatus } from '../index.js'

/**
 * Plain words for the worker's Clinical Bridge panel, in the voice of
 * `anchorText.ts`: what the facility offers, what it asks, and what the
 * worker can do about it. Each function answers from the record alone, so
 * the panel stays a layout.
 */

export function pluralMinutes(minutes: number): string {
  return minutes === 1 ? '1 minute' : `${minutes} minutes`
}

export function pluralPoints(points: number): string {
  return points === 1 ? '1 point' : `${points} points`
}

/** Whether the worker may put a day on their list at all. */
export function canEnroll(bridge: Pick<ClinicalBridgeStatus, 'eligible' | 'rules'>): boolean {
  return bridge.rules.enabled && bridge.eligible
}

/** One sentence on why the worker cannot enroll, or null when they can. */
export function enrollmentBlocker(bridge: Pick<ClinicalBridgeStatus, 'eligible' | 'rules'>): string | null {
  if (!bridge.rules.enabled) {
    return 'Clinical Bridge is not switched on at your facility right now.'
  }
  if (!bridge.eligible) {
    return bridge.rules.eligibilityMode === 'by_employment_type' ? 'At your facility only part-time and PRN staff can pre-bid on-call days while overtime is off, so Clinical Bridge is not open to you.' : 'Your facility has not made you eligible yet. Ask your manager if you would like to be.'
  }
  return null
}

/** The cap and the cutoff, where the facility sets them, as a sentence under the terms. */
export function enrollmentLimits(rules: ClinicalBridgeRules): string | null {
  const parts: string[] = []
  if (rules.maxDaysPerPeriod != null) {
    parts.push(`You can enroll for up to ${rules.maxDaysPerPeriod} ${rules.maxDaysPerPeriod === 1 ? 'day' : 'days'} a month, and only on days you are not scheduled to work.`)
  }
  if (rules.withdrawCutoffHours != null && rules.withdrawCutoffHours > 0) {
    parts.push(`Withdraw at least ${rules.withdrawCutoffHours} hours before the day starts; a later withdrawal goes on your record.`)
  }
  return parts.length ? parts.join(' ') : null
}

/** What a Bridge shift is worth and what it asks, as the facility has set it. */
export function bridgeTerms(rules: ClinicalBridgeRules): string[] {
  const lines = [
    rules.scoreBoostPerShift > 0 ? `Every Bridge shift you work adds ${pluralPoints(rules.scoreBoostPerShift)} to your score.` : 'A Bridge shift counts like any other shift you work.',
    rules.responseWindowMinutes > 0 ? `When a shift opens, everyone enrolled for that day is told at once. The first to accept within ${pluralMinutes(rules.responseWindowMinutes)} gets it.` : 'When a shift opens, everyone enrolled for that day is told at once. The first to accept gets it.',
    `If you accept, you need to be at the facility within ${pluralMinutes(rules.arrivalWindowMinutes)}.`,
    'There is no penalty for declining or for not answering.',
  ]
  return rules.payPremiumEnabled && rules.payPremiumAmount !== null && rules.payPremiumAmount > 0 ? [...lines, `A Bridge shift pays your regular rate plus $${rules.payPremiumAmount} an hour.`] : lines
}

/** The sentence under the calendar about the days on the list. */
export function enrolledSummary(bridge: Pick<ClinicalBridgeStatus, 'preBidDates'>, today: string): string {
  const upcoming = bridge.preBidDates.filter((preBid) => preBid.date >= today).length
  if (upcoming === 0) {
    return 'You are not enrolled for any upcoming days. Tap a date on the calendar to enroll.'
  }
  return upcoming === 1 ? 'You are enrolled for 1 upcoming day. Tap a date to enroll or withdraw.' : `You are enrolled for ${upcoming} upcoming days. Tap a date to enroll or withdraw.`
}

/** Whether a given date may be enrolled for: today or later, and inside this month or next, as the API rules it. */
export function isEnrollableDate(date: string, today: string): boolean {
  if (date < today) {
    return false
  }
  const [year, month] = today.split('-').map(Number)
  const end = new Date(Date.UTC(year ?? 1970, (month ?? 1) + 1, 1)).toISOString().slice(0, 10)
  return date < end
}
